import { m } from '../../lib/motion.js';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { useOnScreen } from '../../hooks/useOnScreen.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * A page section with the standard rhythm — 48px on mobile, 72px on desktop —
 * and the single entry animation the PRD allows: one fade-and-rise on first
 * scroll into view, 24px of travel, once only.
 */
export const Section = ({ children, className, bleed = false, as: Component = 'section', ...props }) => {
  const [ref, seen] = useOnScreen();
  const reduced = useReducedMotion();

  const MotionComponent = m[Component] ?? m.section;

  return (
    <MotionComponent
      ref={ref}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      animate={seen ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: reduced ? 0.25 : 0.5, ease: [0.25, 1, 0.5, 1] }}
      className={cn('py-section lg:py-section-lg', className)}
      {...props}
    >
      <div className={cn(!bleed && 'mx-auto max-w-(--container-content) px-4 sm:px-6')}>{children}</div>
    </MotionComponent>
  );
};

/**
 * Section headings share one shape: a monospace eyebrow, a display title, and an
 * optional link out to the full listing. Consistency here is most of what makes
 * a long home page read as one document.
 */
export const SectionHeading = ({ eyebrow, title, description, action, className }) => (
  <header className={cn('mb-8 flex flex-wrap items-end justify-between gap-x-6 gap-y-3', className)}>
    <div className="max-w-xl">
      {eyebrow && (
        <p className="font-mono text-2xs tracking-[0.16em] text-muted uppercase">{eyebrow}</p>
      )}
      <h2 className="mt-2 font-display text-2xl tracking-[-0.02em] text-ink">{title}</h2>
      {description && <p className="mt-2 text-xs leading-relaxed text-muted">{description}</p>}
    </div>

    {action && (
      <Link
        to={action.to}
        className="group shrink-0 font-mono text-2xs tracking-[0.1em] text-ink uppercase focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {action.label}
        <span aria-hidden="true" className="ml-1.5 inline-block transition-transform group-hover:translate-x-0.5">
          →
        </span>
      </Link>
    )}
  </header>
);

export default Section;
